import { AuthenticatedAPIClient, type APIResponse } from "./authenticated-api";

/**
 * A `faculty` row that is not a khoa: it marks accounts that may see every khoa,
 * and it carries no majors. The server reads it as "unrestricted", so no UI may
 * offer it as something to filter by.
 */
export const BOARD_FACULTY_CODE = "Board";

export interface Faculty {
	code: string;
	name: string;
	order: number;
	active: boolean;
}

export interface FacultyCreateInput {
	code: string;
	name: string;
	order?: number;
	active?: boolean;
}

export interface FacultyUpdateInput {
	name?: string;
	order?: number;
	active?: boolean;
}

export interface Major {
	code: string;
	name: string;
	mssv: string;
	facultyCode: string;
	order: number;
	active: boolean;
}

export interface MajorCreateInput {
	code: string;
	name: string;
	facultyCode: string;
	mssv?: string;
	order?: number;
	active?: boolean;
}

export interface MajorUpdateInput {
	name?: string;
	facultyCode?: string;
	mssv?: string;
	order?: number;
	active?: boolean;
}

export interface Paper {
	id: number;
	name: string;
	order: number;
	active: boolean;
}

export interface PaperCreateInput {
	name: string;
	order?: number;
	active?: boolean;
}

export interface PaperUpdateInput {
	name?: string;
	order?: number;
	active?: boolean;
}

/**
 * A row of an admission method's paper mapping. Includes the joined `paper`
 * so a deactivated paper still resolves to its name — the endpoint returns
 * inactive papers on purpose (see `catalog.constants.ts` on the API side).
 */
export interface MethodPaper {
	methodId: string;
	paperId: number;
	order: number;
	defaultChecked: boolean;
	paper: Paper;
}

export interface MethodPaperInput {
	paperId: number;
	order?: number;
	defaultChecked?: boolean;
}

/**
 * A row of a student's hồ sơ checklist — the union of what their diện
 * currently requires and whatever is already recorded in their `GiayTo`.
 *
 * `recordedOnly: true` means the paper is no longer part of the diện's
 * requirement (retired, or a rule the student no longer satisfies) but was
 * already collected. The backend decides this; the frontend only renders it —
 * see `catalog.controller.ts#listStudentPapers` for why the rule evaluation
 * never happens here.
 */
export interface StudentPaper {
	paperId: number;
	order: number;
	defaultChecked: boolean;
	paper: Paper;
	recordedOnly: boolean;
}

/**
 * Unwraps the `{ success, message, data }` envelope every `/v1/catalog`
 * endpoint returns, throwing `Error(message)` on failure so every caller has
 * one catch shape instead of re-checking `success` everywhere.
 */
async function unwrap<T>(response: Promise<APIResponse<T>>): Promise<T> {
	const result = await response;
	if (!result.success || result.data === undefined) {
		throw new Error(result.message || "Yêu cầu thất bại");
	}
	return result.data;
}

/**
 * Thin typed client for `/v1/catalog`. No component talks to `fetch` or
 * `AuthenticatedAPIClient` directly for these endpoints — this is the one
 * place that knows the URLs and payload shapes.
 */
class CatalogApiService {
	private readonly baseUrl = "/catalog";

	// ---------------------------------------------------------------- faculties

	listFaculties(includeInactive = false): Promise<Faculty[]> {
		return unwrap(
			AuthenticatedAPIClient.get<Faculty[]>(
				`${this.baseUrl}/faculties?includeInactive=${includeInactive}`,
			),
		);
	}

	createFaculty(input: FacultyCreateInput): Promise<Faculty> {
		return unwrap(AuthenticatedAPIClient.post<Faculty>(`${this.baseUrl}/faculties`, input));
	}

	updateFaculty(code: string, input: FacultyUpdateInput): Promise<Faculty> {
		return unwrap(
			AuthenticatedAPIClient.patch<Faculty>(
				`${this.baseUrl}/faculties/${encodeURIComponent(code)}`,
				input,
			),
		);
	}

	deleteFaculty(code: string): Promise<Faculty> {
		return unwrap(
			AuthenticatedAPIClient.delete<Faculty>(
				`${this.baseUrl}/faculties/${encodeURIComponent(code)}`,
			),
		);
	}

	// ------------------------------------------------------------------- majors

	listMajors(params: { facultyCode?: string; includeInactive?: boolean } = {}): Promise<Major[]> {
		const searchParams = new URLSearchParams();
		if (params.facultyCode) searchParams.append("faculty_code", params.facultyCode);
		searchParams.append("includeInactive", String(params.includeInactive ?? false));

		return unwrap(
			AuthenticatedAPIClient.get<Major[]>(`${this.baseUrl}/majors?${searchParams.toString()}`),
		);
	}

	createMajor(input: MajorCreateInput): Promise<Major> {
		return unwrap(AuthenticatedAPIClient.post<Major>(`${this.baseUrl}/majors`, input));
	}

	updateMajor(code: string, input: MajorUpdateInput): Promise<Major> {
		return unwrap(
			AuthenticatedAPIClient.patch<Major>(
				`${this.baseUrl}/majors/${encodeURIComponent(code)}`,
				input,
			),
		);
	}

	deleteMajor(code: string): Promise<Major> {
		return unwrap(
			AuthenticatedAPIClient.delete<Major>(`${this.baseUrl}/majors/${encodeURIComponent(code)}`),
		);
	}

	// ------------------------------------------------------------------- papers

	listPapers(includeInactive = false): Promise<Paper[]> {
		return unwrap(
			AuthenticatedAPIClient.get<Paper[]>(
				`${this.baseUrl}/papers?includeInactive=${includeInactive}`,
			),
		);
	}

	createPaper(input: PaperCreateInput): Promise<Paper> {
		return unwrap(AuthenticatedAPIClient.post<Paper>(`${this.baseUrl}/papers`, input));
	}

	updatePaper(id: number, input: PaperUpdateInput): Promise<Paper> {
		return unwrap(AuthenticatedAPIClient.patch<Paper>(`${this.baseUrl}/papers/${id}`, input));
	}

	deletePaper(id: number): Promise<Paper> {
		return unwrap(AuthenticatedAPIClient.delete<Paper>(`${this.baseUrl}/papers/${id}`));
	}

	// --------------------------------------------------- method → paper mapping

	listMethodPapers(methodId: string): Promise<MethodPaper[]> {
		return unwrap(
			AuthenticatedAPIClient.get<MethodPaper[]>(
				`${this.baseUrl}/admission-methods/${encodeURIComponent(methodId)}/papers`,
			),
		);
	}

	replaceMethodPapers(methodId: string, papers: MethodPaperInput[]): Promise<MethodPaper[]> {
		return unwrap(
			AuthenticatedAPIClient.put<MethodPaper[]>(
				`${this.baseUrl}/admission-methods/${encodeURIComponent(methodId)}/papers`,
				{ papers },
			),
		);
	}

	// --------------------------------------------------- per-student checklist

	listStudentPapers(mssv: string): Promise<StudentPaper[]> {
		return unwrap(
			AuthenticatedAPIClient.get<StudentPaper[]>(
				`${this.baseUrl}/students/${encodeURIComponent(mssv)}/papers`,
			),
		);
	}
}

export const catalogApi = new CatalogApiService();
